import { useEffect, useMemo, useRef } from 'react';
import type { SettingsType } from '../../../shared/api/pipeex/pcfSettingsClient';
import { useHostRibbonCommands, useHostRibbonOwner } from '../../../shared/ui/hostRibbon';
import { selectQuotaLimited, useExportQuotaStore } from '../../../shared/stores/useExportQuotaStore';
import { toHostRibbonWorkspaceTab } from '../lib/mappingPageHostBridge';
import { dispatchMappingHostRibbonCommand } from '../lib/mappingHostRibbonCommands';
import { publishProfileDetails } from '../lib/profileDetailsRequest';
import {
  buildSettingInfoProfileKey,
  parseMappingSettingsType,
} from '../lib/mappingPageProjectProfileUtils';
import { type MappingWorkspaceTabIndex } from '../lib/mappingWorkspacePageModel';
import type { NavigationSource } from '../../../shared/lib/setupRevision';

export interface UseMappingHostRibbonBridgeArgs {
  activeTab: number;
  /**
   * The single guarded entry point for workspace navigation. The bridge never
   * moves the tab itself — a ribbon click is a request like any other.
   */
  requestWorkspaceTabChange: (tab: MappingWorkspaceTabIndex, source: NavigationSource) => void;
  selectedProfileKey: string;
  profiles: Array<{ name: string; settingsType: SettingsType }>;
  profilesLoading: boolean;
  resolvedProfileName: string;
  resolvedSettingsType: SettingsType;
  isSaving: boolean;
  currentSettingsNeedProfileSave: boolean;
  profileActionsDisabled: boolean;
  profileSelectionDisabled: boolean;
  profileLoadDisabled: boolean;
  showMappingWorkspaceSummary: boolean;
  user: { username: string } | null;
  isSigningOut: boolean;
  handleLoadProfileForCurrentProject: (profileKeyOverride?: string) => Promise<boolean>;
  confirmProfileLoad: (profileKeyOverride?: string) => Promise<boolean>;
  lastAppliedProfileKey: string;
  handleWorkspaceSelectedProfileChange: (profileKey: string) => void;
  onSelectedSettingsTypeChange: (value: SettingsType) => void;
  onRefreshProfiles: () => void;
  onSaveProfile: () => void;
  onDeleteProfile: (profileKey?: string) => void;
  onDeleteProfiles: (profileKeys: string[]) => void;
  handleOpenActivePageGuide: () => void;
  handleOpenActivePageTour: () => void;
  handleOpenSummaryWindow: () => void;
  handleSignOut: () => Promise<void>;
  themeContext: { toggleTheme: () => void };
  setHostProfileSaveAsDialogOpen: (open: boolean) => void;
  setHostProfileRenameDialogProfileKey: (profileKey: string | null) => void;
}

export function useMappingHostRibbonBridge(args: UseMappingHostRibbonBridgeArgs): void {
  const {
    activeTab,
    requestWorkspaceTabChange,
    selectedProfileKey,
    profiles,
    profilesLoading,
    resolvedProfileName,
    resolvedSettingsType,
    isSaving,
    currentSettingsNeedProfileSave,
    profileActionsDisabled,
    profileSelectionDisabled,
    profileLoadDisabled,
    showMappingWorkspaceSummary,
    user,
    isSigningOut,
    handleLoadProfileForCurrentProject,
    confirmProfileLoad,
    lastAppliedProfileKey,
    handleWorkspaceSelectedProfileChange,
    onSelectedSettingsTypeChange,
    onRefreshProfiles,
    onSaveProfile,
    onDeleteProfile,
    onDeleteProfiles,
    handleOpenActivePageGuide,
    handleOpenActivePageTour,
    handleOpenSummaryWindow,
    handleSignOut,
    themeContext,
    setHostProfileSaveAsDialogOpen,
    setHostProfileRenameDialogProfileKey,
  } = args;
  const profileLoadInFlightRef = useRef(false);
  // Declared first so the ownership claim is committed before the effects below
  // publish through it — React runs effects in hook declaration order.
  const { publish: publishHostRibbon, isCurrentOwner: ownsHostRibbon } =
    useHostRibbonOwner('mapping-workspace');

  // The exports counter lives in the native chrome, so the entitlement
  // must be loaded with the workspace, not only when the Export page opens.
  const exportsLeftRemaining = useExportQuotaStore((state) => state.remainingExportElements);
  const showExportsLeft = useExportQuotaStore(selectQuotaLimited);

  useEffect(() => {
    void useExportQuotaStore.getState().loadCurrentLicense();
  }, []);

  useHostRibbonCommands('mapping-workspace', true, (payload) =>
    dispatchMappingHostRibbonCommand(
      {
        ownsHostRibbon,
        requestWorkspaceTabChange,
        selectedProfileKey,
        lastAppliedProfileKey,
        profiles,
        profilesLoading,
        isSaving,
        profileLoadDisabled,
        loadInFlight: profileLoadInFlightRef,
        applySelectedSettingsType: onSelectedSettingsTypeChange,
        applySelectedProfileChange: handleWorkspaceSelectedProfileChange,
        loadProfileForCurrentProject: handleLoadProfileForCurrentProject,
        confirmProfileLoad,
        onRefreshProfiles,
        onRequestProfileDetails: (profileKey: string) => {
          void publishProfileDetails(profileKey, profiles);
        },
        onSaveProfile,
        onDeleteProfile,
        onDeleteProfiles,
        handleOpenActivePageGuide,
        handleOpenActivePageTour,
        handleOpenSummaryWindow,
        handleSignOut,
        toggleTheme: () => themeContext.toggleTheme(),
        setHostProfileSaveAsDialogOpen,
        setHostProfileRenameDialogProfileKey,
      },
      payload
    )
  );

  const hostRibbonProfiles = useMemo(
    () =>
      profiles.map((profile) => ({
        key: buildSettingInfoProfileKey(profile),
        name: profile.name,
        settingsType: parseMappingSettingsType(profile.settingsType),
      })),
    [profiles]
  );

  useEffect(() => {
    publishHostRibbon({
      visible: true,
      showWorkspaceTabs: true,
      showWorkflowSteps: false,
      workflowSteps: [],
      activeTab,
      activeTabKey: toHostRibbonWorkspaceTab(activeTab),
      profiles: hostRibbonProfiles,
      profilesLoading,
      selectedProfileKey,
      selectedProfileName: resolvedProfileName,
      selectedSettingsType: resolvedSettingsType,
      isSaving,
      profileHasUnsavedChanges: Boolean(selectedProfileKey && currentSettingsNeedProfileSave),
      profileActionsDisabled,
      profileSelectionDisabled,
      profileLoadDisabled,
      lastAppliedProfileKey,
      showProfiles: true,
      showGuide: true,
      showTour: true,
      showTheme: true,
      showSummary: showMappingWorkspaceSummary,
      canSignOut: Boolean(user),
      isSigningOut,
      userName: user?.username ?? null,
      showExportsLeft,
      exportsLeftText: showExportsLeft ? String(exportsLeftRemaining) : '',
    });
  }, [
    activeTab,
    currentSettingsNeedProfileSave,
    exportsLeftRemaining,
    hostRibbonProfiles,
    isSaving,
    isSigningOut,
    lastAppliedProfileKey,
    profileActionsDisabled,
    profileLoadDisabled,
    profileSelectionDisabled,
    profilesLoading,
    publishHostRibbon,
    resolvedProfileName,
    resolvedSettingsType,
    selectedProfileKey,
    showExportsLeft,
    showMappingWorkspaceSummary,
    user,
  ]);
}
